import { Controller } from "@hotwired/stimulus"
import { showSystemToast } from "../utils/system_toast"

export default class extends Controller {
    static targets = ["flash"]

    connect() {
        this.debounceTimers = new Map()
    }

    disconnect() {
        // Clear any pending debounce timers
        this.debounceTimers.forEach(timer => clearTimeout(timer))
        this.debounceTimers.clear()
    }

    submit(event) {
        const target = event?.target
        const isTextInput = target && (target.tagName === "TEXTAREA" || (target.tagName === "INPUT" && target.type === "text"))
        const isQuiet = target?.dataset.autosaveQuiet === "true"

        // Debounce text inputs to avoid saving on every keystroke
        if (isTextInput) {
            const inputId = target.id || target.name || "default"
            if (this.debounceTimers.has(inputId)) {
                clearTimeout(this.debounceTimers.get(inputId))
            }
            this.debounceTimers.set(inputId, setTimeout(() => {
                this.debounceTimers.delete(inputId)
                this.doSubmit(event, isQuiet)
            }, 800))
            return
        }

        this.doSubmit(event, isQuiet)
    }

    async doSubmit(event, isQuiet = false) {
        const form = this.element
        const url = form.action + ".json"
        const token = document.querySelector("meta[name='csrf-token']")?.content
        const formData = new FormData(form)
        const target = event?.target
        const contextMessage = this.contextMessageFor(target)

        try {
            const response = await fetch(url, {
                method: form.method || "patch",
                headers: {
                    "Accept": "application/json",
                    "X-CSRF-Token": token
                },
                body: formData,
                credentials: "same-origin"
            })

            if (response.ok) {
                if (!this.handleStatusSuccess(target, contextMessage || "Settings saved")) {
                    // Skip flash for quiet inputs (text fields with autosave_quiet)
                    if (!isQuiet) {
                        this.showFlash(contextMessage || "Settings saved", "success")
                    }
                }
            } else {
                const data = await response.json().catch(() => ({}))
                if (!this.handleStatusError(target, data.errors?.join(", ") || "Save failed")) {
                    this.showFlash(data.errors?.join(", ") || "Save failed", "danger")
                }
            }
        } catch (e) {
            if (!this.handleStatusError(target, "Save failed")) {
                this.showFlash("Save failed", "danger")
            }
        }
    }

    showFlash(message, level) {
        const toastLevel = level === "danger" ? "danger" : "success"
        showSystemToast(message, { level: toastLevel, duration: 1500 })
    }

    contextMessageFor(target) {
        if (!target) return null
        const label = target.dataset.autosaveLabel
        if (!label) return null

        if (target.type === "checkbox") {
            // Check for custom messages first
            const enabledMsg = target.dataset.autosaveEnabledMessage
            const disabledMsg = target.dataset.autosaveDisabledMessage
            if (target.checked && enabledMsg) {
                return enabledMsg
            }
            if (!target.checked && disabledMsg) {
                return disabledMsg
            }
            return target.checked ? `${label} enabled` : `${label} disabled`
        }
        if (target.type === "radio") {
            const optionLabel = target.dataset.autosaveOptionLabel
            return `${label}: ${optionLabel || target.value}`
        }
        return null
    }

    handleStatusSuccess(target, message) {
        if (!target) return false
        const statusId = target.dataset.autosaveStatusTarget
        if (!statusId) return false
        this.updateStatus(statusId, `Autosaved just now`, false)
        return true
    }

    handleStatusError(target, message) {
        if (!target) return false
        const statusId = target.dataset.autosaveStatusTarget
        if (!statusId) return false
        this.updateStatus(statusId, message, true)
        return true
    }

    updateStatus(id, message, isError) {
        const el = document.getElementById(id)
        if (!el) return
        el.textContent = message
        el.classList.toggle("text-danger", isError)
        el.classList.toggle("text-muted", !isError)
    }
}

